/**
 * Los consejos de Igor por paso (la clave es la ruta del paso). Borrador revisado en docs/consejos-igor.md.
 * Textos fijos en español; su inglés está en idioma/en/igor.ts (lo exige consejos.test.ts).
 */
export const CONSEJOS: Record<string, string[]> = {
  negocio: [
    'Cuéntame a quién le vendes como se lo explicarías a un amigo. Entre más concreto, mejores posts te hago.',
    '¿Qué te hace distinto? Piensa en lo que tus clientes te dicen cuando te recomiendan.',
    'Si tienes Instagram o página web, pónmelos: los leo y te ahorro preguntas.',
  ],
  estrategia: [
    'No tienes que leerla toda hoy. Mira los temas: si alguno no va con tu negocio, pídeme cambiarlo.',
    'Las frases de ejemplo están escritas como hablan tus clientes. Si alguna te suena rara, dímelo.',
    'Pocas publicaciones venden directo: la mayoría enseñan o acompañan. Así la gente confía antes de comprar.',
  ],
  redes: [
    'No te asustes con los números: son tu punto de partida y desde aquí medimos cuánto mejoras.',
    'Fíjate en el formato que mejor te funciona: te lo señalo para que hagamos más de eso.',
    'Puedes conectar más de una red: así veo dónde te está yendo mejor.',
  ],
  identidad: [
    'Sube 3 publicaciones que te encanten, aunque no sean tuyas. Así aprendo tu estilo.',
    'Si tienes logo en fondo claro y en fondo oscuro, sube los dos: se verá bien en cualquier diseño.',
    '¿Tus colores son exactos? Escríbelos y los uso tal cual en todos tus diseños.',
  ],
  presentador: [
    'Elige a quien se parezca a tus clientes: la gente confía en quien se le parece.',
    'Puedes tener más de una persona y cambiar quién sale en cada video.',
    'Si quieres salir tú, sube una foto tuya con buena luz, de frente y sin gafas de sol.',
  ],
  mes: [
    'Con 3 publicaciones por semana basta: la constancia vale más que la cantidad.',
    '¿Ese día no te sirve? Puedes mover la publicación a otro sin rehacer el plan.',
    '¿Tienes una fecha especial este mes (lanzamiento, promoción, aniversario)? Cuéntamela con «Pedir un cambio» y la incluyo.',
  ],
  textos: [
    'Lee el texto en voz alta. Si no suena como hablas tú, pídeme el cambio con tus palabras.',
    'La primera frase es la que hace que alguien se detenga. Si no te da curiosidad a ti, tampoco a ellos.',
    '¿Ves algo entre corchetes, como [TU PRECIO]? Es un dato que solo tú sabes: complétalo antes de aprobar.',
  ],
  disenos: [
    'Míralos en tu celular, que es donde los verán tus clientes.',
    'Si no te gusta un diseño, dime qué cambiarías («más claro», «otra foto»). No tienes que saber de diseño.',
    'En los carruseles, la primera lámina es la portada: tiene que dar ganas de deslizar.',
  ],
  videos: [
    'Los primeros 3 segundos deciden si te ven. Revisa que el comienzo dé curiosidad.',
    'Antes de grabar te muestro las escenas en imagen: si alguna no te gusta, cámbiala ahí y ahorras créditos.',
    'Puedes cambiar los textos y los subtítulos del video sin volver a grabarlo.',
  ],
  publicar: [
    'Conecta tus redes una sola vez y yo publico a la hora que elegiste.',
    'Las mejores horas dependen de tu público. Empieza con las que te propongo y las ajustamos según tus resultados.',
    '¿Cambiaste de idea? Puedes mover una publicación a otro día antes de que salga.',
  ],
  medicion: [
    'Mira la tendencia del mes, no un solo post: un mal día no dice nada.',
    'Guardados y compartidos pesan más que los «me gusta»: dicen que tu contenido le sirvió a alguien.',
    'Anota qué funcionó mejor este mes y pídeme más de eso cuando armemos el plan del siguiente.',
  ],
};

/**
 * Consejos para el día, pensados para quien lleva sus propias redes: un título corto que se lee de un vistazo y
 * una explicación de una línea. Rotan solos: cada día empieza por uno distinto y se pueden pasar con las flechas.
 * Son fijos a propósito, sin IA, para que no cuesten nada.
 */
export const TIPS: ReadonlyArray<{ titulo: string; texto: string }> = [
  { titulo: 'Responde en la primera hora', texto: 'Es cuando más gente está viendo tu publicación, y cada respuesta la mueve más.' },
  { titulo: 'Los 3 primeros segundos mandan', texto: 'Empieza tu reel por lo más interesante, no por el saludo.' },
  { titulo: 'Luz de ventana, siempre', texto: 'Una foto de tu producto con luz natural vale más que diez con flash.' },
  { titulo: 'Tus mensajes son ideas', texto: 'Guarda las preguntas que te hacen: cada una es una publicación que ya sabes que interesa.' },
  { titulo: 'Di qué quieres que hagan', texto: 'Cierra cada publicación con una acción: escribir, guardar, compartir o visitar.' },
  { titulo: 'Constancia antes que cantidad', texto: 'Tres veces por semana, todas las semanas, gana a diez un mes y nada el siguiente.' },
  { titulo: 'Muestra a tu equipo', texto: 'La gente le compra a gente. Deja ver quién está detrás de tu negocio.' },
  { titulo: 'Enseña algo útil', texto: 'Un carrusel que enseña se guarda y se comparte: es el que más te hace crecer.' },
  { titulo: 'Repite lo que funcionó', texto: 'Mira qué publicación te trajo más mensajes este mes y haz otra parecida.' },
  { titulo: 'Subtítulos en todo', texto: 'La mayoría ve los videos sin sonido. Si no hay texto, no se entiende.' },
  { titulo: 'Nadie quiere esperar', texto: 'Contesta los mensajes aunque sea con un "ya te respondo".' },
  { titulo: 'Pide una reseña', texto: 'Un cliente feliz que cuenta su experiencia vale más que cualquier anuncio.' },
  { titulo: 'Historias y feed, cada uno a lo suyo', texto: 'Las historias para el día a día; el feed para lo que quieres que dure.' },
  { titulo: 'Léelo en voz alta', texto: 'Si suena raro dicho, suena raro escrito. Ajústalo antes de publicar.' },
  { titulo: 'Pon el precio', texto: 'Un precio claro te ahorra muchos mensajes de "¿cuánto vale?".' },
  { titulo: 'Cuenta el antes y el después', texto: 'La gente entiende mejor un cambio que una lista de beneficios.' },
  { titulo: 'Fija lo mejor arriba', texto: 'Deja en tu perfil las tres publicaciones que mejor explican lo que haces.' },
  { titulo: 'Las fechas de tu ciudad', texto: 'Aprovecha las celebraciones locales, no solo las del calendario.' },
  { titulo: 'Cambia el comienzo', texto: 'Si algo no funcionó, prueba otro inicio antes de rehacerlo todo.' },
  { titulo: 'Una biografía que vende', texto: 'Qué haces, para quién y cómo contactarte. En ese orden.' },
  { titulo: 'Vertical y de cerca', texto: 'En el celular, lo pequeño no se ve. Acércate a lo que importa.' },
  { titulo: 'Comenta en otras cuentas', texto: 'Hablar con tus clientes y vecinos en sus publicaciones también es hacer comunidad.' },
  { titulo: 'Pregunta antes de lanzar', texto: 'Las preguntas en historias te dicen qué le importa a tu gente.' },
  { titulo: 'Menos redes, mejor atendidas', texto: 'Dos redes bien cuidadas valen más que cinco abandonadas.' },
  { titulo: 'Tu público no lo vio todo', texto: 'Repetir una idea que funcionó no aburre: la mayoría no la vio la primera vez.' },
  { titulo: 'Enseña el proceso', texto: 'Mostrar cómo haces tu producto o tu servicio genera confianza.' },
  { titulo: 'La primera línea es el título', texto: 'Si engancha, leen el resto del texto.' },
  { titulo: 'Tus números, una vez por semana', texto: 'Así ves la tendencia y no te angustia el día a día.' },
  { titulo: 'Agradece en público', texto: 'Mencionar a quien te recomienda invita a que otros lo hagan.' },
  { titulo: 'Lo obvio para ti, sorprende', texto: 'Lo que das por sabido de tu negocio puede ser justo lo que tu cliente no sabe.' },
];

/** El tip con el que empieza cada día: cambia a medianoche y es el mismo para todas tus marcas. */
export function tipDeHoy(fecha = new Date()): number {
  const dia = Math.floor(Date.UTC(fecha.getFullYear(), fecha.getMonth(), fecha.getDate()) / 86_400_000);
  return dia % TIPS.length;
}
